import { Button, Divider, Image, VStack } from '@chakra-ui/react';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { useMyProfileModel } from '@common/models/MyProfileModel';
import { usePricingPlanModel } from '@common/models/PricingPlanModel';
import currency from '@common/utility/currency';
import { BankDetailStatus } from '@services/appsync/types';
import React, { ChangeEvent, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import SplitView from 'src/apps/invest/common/shells/SplitView';
import { ROUTES } from 'src/apps/invest/config';
import { PAGES } from '../../constants';
import { useCheckoutSFRViewModel } from '../../models/CheckoutSFRViewModel';
import Confirm from '../components/Confirm';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/Section';
import Summary from '../components/Summary';
import { CheckoutSFRLoadingPage } from './CheckoutSFRLoadingPage';
import { Helmet } from 'react-helmet';

export const CheckoutSFRConfirmationPage: React.FC = () => {
  const [isLoading, setIsloading] = useState(false);
  const viewModel = useCheckoutSFRViewModel();
  const bankAccountsModel = useBankAccountsModel();
  const form = useForm<{ confirmed: boolean; signature: string }>();
  const hasABankCard = bankAccountsModel.bankAccounts.some(
    (account) => account.default && account.status === BankDetailStatus.VERIFIED,
  );
  const signatureWatch = useWatch({ control: form.control, name: 'signature' });
  const myProfileModel = useMyProfileModel();
  const investorModel = useInvestorAccountModel();
  const hasSignature = myProfileModel.storedSignatureUrl || (signatureWatch && signatureWatch.length > 0);
  const hasConfirmed = viewModel.confirmAgreement;
  const bankAccount = bankAccountsModel.getDefaultBankAccount();
  const pricingPlanModel = usePricingPlanModel();
  const thumbnail = viewModel.property?.sfr?.info?.images?.[0];
  const documents = viewModel.property?.documents?.filter((doc) => doc.show_on_checkout) ?? [];
  documents.unshift({
    __typename: 'SubscriptionOfferDocument',
    title: 'Subscription Agreement',
    url: viewModel.property?.subscription_doc?.download_url ?? '',
  });
  const sharesSelected = viewModel.amount / Number(viewModel.property?.share_price ?? 0);

  const handleFormSubmission = form.handleSubmit(async () => {
    setIsloading(true);
    await viewModel.invest();
  });

  const handleSignatureSave = (dataUrl: string, blob: Blob) => {
    form.setValue('signature', dataUrl);
    viewModel.setNewSignatureDataUrl(dataUrl);
    viewModel.setNewSignatureBlob(blob);
  };

  const handleSignatureClear = () => {
    form.setValue('signature', '');
    viewModel.setNewSignatureDataUrl(undefined);
    viewModel.setNewSignatureBlob(undefined);
  };

  const handleAgreementChange = (e: ChangeEvent<HTMLInputElement>) => {
    form.setValue('confirmed', e.target.checked);
    viewModel.setConfirmAgreement(e.target.checked);
  };

  if (isLoading) return <CheckoutSFRLoadingPage />;

  return (
    <SplitView.UI columnRatio={60}>
      <SplitView.LeftColumn>
        <Helmet>
          <title>Confirm your investment in {viewModel.property?.display_name ?? ''} | Concreit</title>
        </Helmet>
        <Confirm.Form onSubmit={handleFormSubmission}>
          <Header
            title={`Invest in ${viewModel.property?.display_name}`}
            breadCrumb={
              viewModel.pricingPlan ? (
                <Navigation
                  links={[
                    { label: 'Amount', onClick: () => viewModel.setPage(PAGES.amount) },
                    { label: 'Pricing Plan', onClick: () => viewModel.setPage(PAGES.pricingPlan) },
                    { isCurrent: true, label: 'Confirm' },
                  ]}
                />
              ) : (
                <Navigation
                  links={[
                    { label: 'Amount', onClick: () => viewModel.setPage(PAGES.amount) },
                    { isCurrent: true, label: 'Confirm' },
                  ]}
                />
              )
            }
          />
          {documents.length > 0 && (
            <Section icon="file" title="Review Documents" description="Please revew the documents listed.">
              <Confirm.Documents documents={documents.map((doc) => [doc.title, doc.url])} />
            </Section>
          )}
          <Divider />
          {myProfileModel.storedSignatureUrl ? (
            <Section icon="signature" title="Stored Signature">
              <Image
                maxW={['100%', 250, 250, 300]}
                src={myProfileModel.storedSignatureUrl}
                alt={`${investorModel.investorAccount?.individual_details?.first_name} ${investorModel.investorAccount?.individual_details?.last_name}`}
              />
              <Confirm.Agreement onChange={handleAgreementChange} />
            </Section>
          ) : (
            <Section icon="signature" title="Sign Documents" description={`Please sign below:`}>
              <Confirm.Signature onSave={handleSignatureSave} onClear={handleSignatureClear} />
              <Confirm.Agreement onChange={handleAgreementChange} />
            </Section>
          )}
        </Confirm.Form>
      </SplitView.LeftColumn>
      <SplitView.RightColumn>
        <VStack position={['static', 'static', 'static', 'sticky']} top="0px" gap={'31px'} width="100%">
          <Confirm.Form onSubmit={handleFormSubmission}>
            <Summary.Container label="Purchase Summary">
              <Summary.Item
                label={viewModel.property?.display_name as string}
                description={`${sharesSelected} Shares`}
                image={thumbnail?.url}
                value={currency.format(viewModel.amount)}
              />
              {bankAccount?.name && (
                <Summary.Item
                  label="Account"
                  value={`${bankAccount?.name} - ${bankAccount?.account_type} (...${bankAccount?.account_number || ''})`}
                />
              )}
              {viewModel.pricingPlan && (
                <Summary.Item label="Pricing Plan" value={pricingPlanModel.getFriendlyName(viewModel.pricingPlan)} />
              )}
            </Summary.Container>
            <Confirm.SubmitButton isDisabled={!hasABankCard || !hasSignature || !hasConfirmed || isLoading} />
            <Button as={Link} to={ROUTES.properties} variant="link" color="grey">
              Cancel Investment
            </Button>
          </Confirm.Form>
        </VStack>
      </SplitView.RightColumn>
    </SplitView.UI>
  );
};
